/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Tiptap Rich Editor & Draft Document Store (Task 10.2.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { create } from 'zustand';

export type SaveStatus = 'saved' | 'saving' | 'unsaved' | 'error';

export interface EditorStats {
  words: number;
  characters: number;
  readingTimeMinutes: number;
}

export function calculateEditorStats(text: string): EditorStats {
  const trimmed = text.trim();
  if (!trimmed) {
    return { words: 0, characters: 0, readingTimeMinutes: 0 };
  }

  // Count words across CJK, Vietnamese, and Latin boundaries
  const wordsArray = trimmed.match(/[\p{L}\p{N}_\-]+/gu) || [];
  const words = wordsArray.length;
  const characters = trimmed.length;
  const readingTimeMinutes = Math.max(1, Math.ceil(words / 200));

  return { words, characters, readingTimeMinutes };
}

export interface DocumentDraft {
  documentId: string;
  title: string;
  contentHtml: string;
  contentJson?: Record<string, unknown>;
  updatedAt: number;
}

interface EditorState {
  documentId: string;
  title: string;
  contentHtml: string;
  contentJson: Record<string, unknown> | null;
  status: SaveStatus;
  lastSavedAt: number | null;
  stats: EditorStats;
  isWordCountVisible: boolean;

  // Actions
  setTitle: (title: string) => void;
  setContent: (html: string, json?: Record<string, unknown>) => void;
  setStatus: (status: SaveStatus) => void;
  setStats: (stats: EditorStats) => void;
  toggleWordCountVisibility: () => void;
  saveDraft: () => boolean;
  loadDraft: (docId?: string) => boolean;
  resetEditor: () => void;
}

const DEFAULT_DOC_ID = 'doc_draft_01';
const DEFAULT_TITLE = 'Bản Thảo Chiến Lược Nội Dung AI 2026';
const DEFAULT_INITIAL_HTML = `
<h2>Chào mừng đến với Trình Soạn Thảo Thông Minh Sophia AI</h2>
<p>Đây là môi trường soạn thảo tài liệu cao cấp chuẩn Edge-Native. Hãy thử nhấn <code>/</code> ở đầu dòng để mở <strong>Menu Lệnh Tắt Slash Commands</strong>.</p>
<ul data-type="taskList">
  <li data-checked="true"><label><input type="checkbox" checked="checked"><span></span></label><div><p>Khởi tạo Lõi Tiptap Editor và bộ Extensions cao cấp</p></div></li>
  <li data-checked="true"><label><input type="checkbox" checked="checked"><span></span></label><div><p>Tích hợp hệ thống Slash Commands (gõ / để mở)</p></div></li>
  <li data-checked="false"><label><input type="checkbox"><span></span></label><div><p>Thử nghiệm Auto-Save tức thì vào LocalStorage</p></div></li>
</ul>
<blockquote>
  <p>Sức mạnh AI kết hợp với trải nghiệm gõ phím mượt mà tại rìa mạng Cloudflare.</p>
</blockquote>
`;

export const useEditorStore = create<EditorState>((set, get) => ({
  documentId: DEFAULT_DOC_ID,
  title: DEFAULT_TITLE,
  contentHtml: DEFAULT_INITIAL_HTML,
  contentJson: null,
  status: 'saved',
  lastSavedAt: Date.now(),
  stats: calculateEditorStats(DEFAULT_INITIAL_HTML.replace(/<[^>]+>/g, ' ')),
  isWordCountVisible: true,

  setTitle: (title: string) => {
    set({ title, status: 'unsaved' });
  },

  setContent: (html: string, json?: Record<string, unknown>) => {
    const rawText = html.replace(/<[^>]*>/g, ' ');
    const stats = calculateEditorStats(rawText);
    set({
      contentHtml: html,
      contentJson: json ?? get().contentJson,
      stats,
      status: 'unsaved',
    });
  },

  setStatus: (status: SaveStatus) => {
    if (status === 'saved') {
      set({ status, lastSavedAt: Date.now() });
    } else {
      set({ status });
    }
  },

  setStats: (stats: EditorStats) => {
    set({ stats });
  },

  toggleWordCountVisibility: () => {
    set((state) => ({ isWordCountVisible: !state.isWordCountVisible }));
  },

  saveDraft: () => {
    const { documentId, title, contentHtml, contentJson } = get();
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const draft: DocumentDraft = {
          documentId,
          title,
          contentHtml,
          contentJson: contentJson ?? undefined,
          updatedAt: Date.now(),
        };
        window.localStorage.setItem(`sofia_draft_${documentId}`, JSON.stringify(draft));
      }
      set({ status: 'saved', lastSavedAt: Date.now() });
      return true;
    } catch (err) {
      console.warn('[useEditorStore] Failed to save draft locally:', err);
      set({ status: 'error' });
      return false;
    }
  },

  loadDraft: (docId?: string) => {
    const targetId = docId || get().documentId;
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const savedRaw = window.localStorage.getItem(`sofia_draft_${targetId}`);
        if (savedRaw) {
          const draft = JSON.parse(savedRaw) as DocumentDraft;
          const rawText = draft.contentHtml.replace(/<[^>]*>/g, ' ');
          set({
            documentId: draft.documentId,
            title: draft.title,
            contentHtml: draft.contentHtml,
            contentJson: draft.contentJson ?? null,
            stats: calculateEditorStats(rawText),
            status: 'saved',
            lastSavedAt: draft.updatedAt,
          });
          return true;
        }
      }
      return false;
    } catch (err) {
      console.warn('[useEditorStore] Failed to load draft:', err);
      return false;
    }
  },

  resetEditor: () => {
    set({
      documentId: DEFAULT_DOC_ID,
      title: DEFAULT_TITLE,
      contentHtml: DEFAULT_INITIAL_HTML,
      contentJson: null,
      status: 'saved',
      lastSavedAt: Date.now(),
      stats: calculateEditorStats(DEFAULT_INITIAL_HTML.replace(/<[^>]+>/g, ' ')),
    });
  },
}));
